import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { contentService } from "@/services/contentService";
import { forumsService } from "@/services/forumsService";
import { eventsService } from "@/services/eventsService";
import { unitsService } from "@/services/unitsService";
import { sponsorsService } from "@/services/sponsorsService";
import { bannersService } from "@/services/bannersService";
import { formsService } from "@/services/formsService";

const STATIC_STALE = 30 * 60 * 1000;
const LIST_STALE = 10 * 60 * 1000;
const LIVE_STALE = 5 * 60 * 1000;

/** True when every query is still waiting for its first result (no cached data). */
export function isInitialLoading(queries) {
  return queries.some((q) => q.isPending);
}

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => contentService.getSettings(),
    staleTime: STATIC_STALE,
  });
}

export function useHomeSections() {
  return useQuery({
    queryKey: queryKeys.homeSections,
    queryFn: () => contentService.getHomeSections(),
    staleTime: STATIC_STALE,
  });
}

export function useAbout() {
  return useQuery({
    queryKey: queryKeys.about,
    queryFn: () => contentService.getAbout(),
    staleTime: STATIC_STALE,
  });
}

export function useForums() {
  return useQuery({
    queryKey: queryKeys.forums,
    queryFn: () => forumsService.listForums(),
    staleTime: LIST_STALE,
  });
}

export function useForum(slug) {
  return useQuery({
    queryKey: queryKeys.forum(slug),
    queryFn: () => forumsService.getForum(slug),
    enabled: Boolean(slug),
    staleTime: LIST_STALE,
  });
}

export function useForumPosts(slug) {
  return useQuery({
    queryKey: queryKeys.forumPosts(slug),
    queryFn: () => forumsService.listPosts(slug),
    enabled: Boolean(slug),
    staleTime: LIVE_STALE,
  });
}

export function useForumDocuments(slug) {
  return useQuery({
    queryKey: queryKeys.forumDocuments(slug),
    queryFn: () => forumsService.listDocuments(slug),
    enabled: Boolean(slug),
    staleTime: LIST_STALE,
  });
}

export function useEvents() {
  return useQuery({
    queryKey: queryKeys.events,
    queryFn: () => eventsService.listEvents(),
    staleTime: LIVE_STALE,
  });
}

export function useUpcomingEvents(limit = 3) {
  return useQuery({
    queryKey: queryKeys.upcomingEvents(limit),
    queryFn: () => eventsService.listUpcoming(limit),
    staleTime: LIVE_STALE,
  });
}

export function useEvent(slug) {
  return useQuery({
    queryKey: queryKeys.event(slug),
    queryFn: () => eventsService.getEvent(slug),
    enabled: Boolean(slug),
    staleTime: LIVE_STALE,
  });
}

export function useForumEvents(forumSlug) {
  return useQuery({
    queryKey: queryKeys.forumEvents(forumSlug),
    queryFn: () => eventsService.listByForum(forumSlug),
    enabled: Boolean(forumSlug),
    staleTime: LIVE_STALE,
  });
}

export function useUnitEvents(unitSlug) {
  return useQuery({
    queryKey: queryKeys.unitEvents(unitSlug),
    queryFn: () => eventsService.listByUnit(unitSlug),
    enabled: Boolean(unitSlug),
    staleTime: LIVE_STALE,
  });
}

export function useUnits() {
  return useQuery({
    queryKey: queryKeys.units,
    queryFn: () => unitsService.listUnits(),
    staleTime: LIST_STALE,
  });
}

export function useFeaturedUnits(limit = 4) {
  return useQuery({
    queryKey: queryKeys.featuredUnits(limit),
    queryFn: () => unitsService.listFeatured(limit),
    staleTime: LIST_STALE,
  });
}

export function useUnit(slug) {
  return useQuery({
    queryKey: queryKeys.unit(slug),
    queryFn: () => unitsService.getUnit(slug),
    enabled: Boolean(slug),
    staleTime: LIST_STALE,
  });
}

export function useUnitPosts(slug) {
  return useQuery({
    queryKey: queryKeys.unitPosts(slug),
    queryFn: () => unitsService.listUnitPosts(slug),
    enabled: Boolean(slug),
    staleTime: LIVE_STALE,
  });
}

export function useSponsors() {
  return useQuery({
    queryKey: queryKeys.sponsors,
    queryFn: () => sponsorsService.listSponsors(),
    staleTime: LIST_STALE,
  });
}

export function useFeaturedSponsors(limit = 4) {
  return useQuery({
    queryKey: queryKeys.featuredSponsors(limit),
    queryFn: () => sponsorsService.listFeatured(limit),
    staleTime: LIST_STALE,
  });
}

export function useBanners(placement) {
  return useQuery({
    queryKey: queryKeys.banners(placement),
    queryFn: () => bannersService.listByPlacement(placement),
    enabled: Boolean(placement),
    staleTime: LIST_STALE,
  });
}

export function useActiveForms() {
  return useQuery({
    queryKey: queryKeys.activeForms,
    queryFn: () => formsService.listActiveForms(),
    staleTime: STATIC_STALE,
  });
}

export function useMemberCount() {
  return useQuery({
    queryKey: queryKeys.memberCount,
    queryFn: () => unitsService.getTotalMemberCount(),
    staleTime: LIST_STALE,
  });
}

export function useUnitCount() {
  return useQuery({
    queryKey: queryKeys.unitCount,
    queryFn: () => unitsService.getPublishedUnitCount(),
    staleTime: LIST_STALE,
  });
}
